import {
  createContext,
  FC,
  PropsWithChildren,
  ReactElement,
  useMemo,
  useState,
} from 'react';
import { MdClose } from 'react-icons/md';
import { Box, Flex, Heading, Icon } from '@chakra-ui/react';

import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { useResizeObserver } from '~/core/shared/hooks/useResizeObserver';
import { Header } from './layout/Header/Header';

export type LayoutProps = PropsWithChildren<{
  subheader?: ReactElement;
  sidebarModal?: ReactElement;
  selectedNavItem?: string;
}>;

type LayoutContextType = {
  selectedNavItem?: string;
  openSidebarModalComponent: (title: string, component: ReactElement) => void;
  closeSidebarModalComponent: () => void;
  sidebarModalOpen: boolean;
};

export const LayoutContext = createContext<LayoutContextType>(
  {} as LayoutContextType,
);

export const Layout: FC<LayoutProps> = ({
  children,
  subheader,
  selectedNavItem,
}) => {
  const { onSetRef: onSetHeaderRef, height: headerHeight } =
    useResizeObserver('height');
  const { onSetRef: onSetSidebarRef, width: sidebarWidth } =
    useResizeObserver('width');

  const [sidebarModal, setSidebarModal] = useState<ReactElement>();
  const [sidebarTitle, setSidebarTitle] = useState<string>();

  // TODO: @haxxxton.. this is the whole purpose of `createPortal`.. we should be using that
  // instead of passing whole components around in useState.. gross
  const openSidebarModalComponent = useEventHandler(
    (title: string, component: ReactElement): void => {
      let shouldOpen = true;
      if (sidebarTitle && sidebarTitle !== title) {
        shouldOpen = window.confirm(
          `This action will close the prompt "${sidebarTitle}". Are you sure you want to replace it with "${title}"?`,
        );
      }
      if (shouldOpen) {
        setSidebarTitle(title);
        setSidebarModal(component);
      }
    },
  );
  const closeSidebarModalComponent = useEventHandler((): void => {
    setSidebarTitle(undefined);
    setSidebarModal(undefined);
  });

  const layoutContextValue = useMemo(
    () => ({
      closeSidebarModalComponent,
      openSidebarModalComponent,
      selectedNavItem,
      sidebarModalOpen: !!sidebarModal,
    }),
    [
      closeSidebarModalComponent,
      openSidebarModalComponent,
      selectedNavItem,
      !!sidebarModal,
    ],
  );

  const normalizedSidebarWidth = sidebarModal ? (sidebarWidth ?? 0) : 0;

  return (
    <LayoutContext.Provider value={layoutContextValue}>
      <Flex direction="row">
        <Flex
          color="black"
          direction="column"
          layerStyle="layout"
          minH="100vh"
          overflowX="hidden"
          w="full"
        >
          <Box
            ref={onSetHeaderRef}
            position="fixed"
            width={
              normalizedSidebarWidth
                ? `calc(100% - ${normalizedSidebarWidth}px)`
                : '100%'
            }
            zIndex={20}
          >
            <Header />
            {subheader || ''}
          </Box>

          {children && (
            <Box
              mr={normalizedSidebarWidth ? `${normalizedSidebarWidth}px` : 0}
              mt={headerHeight ? `${headerHeight}px` : 0}
            >
              {children}
            </Box>
          )}
        </Flex>
        {sidebarModal && (
          <Box
            ref={onSetSidebarRef}
            bgColor="white"
            borderLeft="1px solid"
            borderLeftColor="gray.200"
            color="black"
            height="100%"
            overflow="auto"
            position="fixed"
            right={0}
            style={{ boxShadow: '-5px 0px 5px 1px rgba(0,0,0,0.1)' }}
            zIndex={100}
          >
            <Flex
              borderBottom="1px solid"
              borderColor="gray.200"
              direction="row"
              justifyContent="space-between"
              p="4"
            >
              <Heading as="h3" size="md">
                {sidebarTitle}
              </Heading>
              <Icon
                as={MdClose}
                boxSize="16px"
                cursor="pointer"
                fontSize="16px"
                ml="6"
                mt="5px"
                onClick={closeSidebarModalComponent}
              />
            </Flex>
            <Box p="4">{sidebarModal}</Box>
          </Box>
        )}
      </Flex>
    </LayoutContext.Provider>
  );
};
